import { useEffect, useMemo, useState } from 'react';
import { createResource,getResources,deleteResource, updateResource} from '../../../router/api';
import QuoteDetail from './QuoteDetail';
import CancelQuoteModal from './CancelQuoteModal';
import ConvertToBCCModal from './ConvertToBCCModal';
import toast, { Toaster } from 'react-hot-toast';

interface QuotesListProps {
  onEdit: (quote: any) => void;
}

interface Devis {
  id: number;
  numero: string;
  client: {
    id: number;
    raison_sociale: string;
    email: string;
    telephone?: string;
    code: string;
    whatsapp?: string;
    encours_actuel?: number;
    plafond_credit?: number;
  };
  parent_devis_id: number;
  commercial: { id: number; name: string } | null;
  date_devis: string;
  date_validite: string;
  remise_globale: number;
  statut: 'BROUILLON' | 'ENVOYE' | 'ACCEPTE' | 'REFUSE' | 'EXPIRE' | 'ANNULE';
  total_ttc: number;
  total_ht?: number;        // Ajouté pour éviter recalcul côté front
  total_tva?: number;     // Ajouté pour précision
  notes_client?: string;
  lignes: Array<{
    reference: string;
    designation: string;
    quantite: number;
    prix_unitaire_ht: number;
    remise_percentage?: number;     // Support remise % (optionnel)
    remise_montant?: number;        // Support remise montant (optionnel)
    total_ht: number;
  }>;
}

// ================== CONSTANTES ==================
const STATUS_LABELS: Record<string, string> = {
  BROUILLON: 'Brouillon',
  ENVOYE: 'Envoyé',
  ACCEPTE: 'Accepté',
  REFUSE: 'Refusé',
  EXPIRE: 'Expiré',
  ANNULE: 'Annulé',
  CONVERTI: 'Converti',
};

const STATUS_COLORS: Record<string, string> = {
  BROUILLON: 'bg-gray-100 text-gray-800',
  ENVOYE: 'bg-blue-100 text-blue-800',
  ACCEPTE: 'bg-green-100 text-green-800',
  REFUSE: 'bg-red-100 text-red-800',
  EXPIRE: 'bg-orange-100 text-orange-800',
  ANNULE: 'bg-gray-200 text-gray-700',
  CONVERTI: 'bg-purple-100 text-purple-800',
};

// ================== HELPERS ==================
const getDisplayedStatus = (quote: any): keyof typeof STATUS_LABELS => {
  if (
    quote.date_validite &&
    new Date(quote.date_validite) < new Date() &&
    quote.statut === 'ENVOYE'
  ) {
    return 'EXPIRE';
  }
  return quote.statut;
};

interface QuotesListProps {
  onEdit: (quote: any) => void;
}

export default function QuotesList({ onEdit }: QuotesListProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [quotes, setQuotes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [sendingId, setSendingId] = useState<number | null>(null);

  const [viewMode, setViewMode] = useState<'list' | 'detail'>('list');
  const [selectedQuote, setSelectedQuote] = useState<Devis | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [quoteToCancel, setQuoteToCancel] = useState<any>(null);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [quoteToConvert, setQuoteToConvert] = useState<any>(null);


  // ================== FETCH ==================
  const fetchQuotes = async () => {
    setLoading(true);
    const response = await getResources('devis-bcc');
    const rows = Array.isArray(response.data?.data) ? response.data.data : [];
    setQuotes(rows);
    setLoading(false);
  };

  useEffect(() => {
    fetchQuotes();
  }, []);

  // ================== DATA MÉTIER ==================
  const enrichedQuotes = useMemo(() => {
    return quotes.map(q => ({
      ...q,
      displayedStatut: getDisplayedStatus(q),
      isExpired:
        q.date_validite &&
        new Date(q.date_validite) < new Date() &&
        q.statut === 'ENVOYE',
    }));
  }, [quotes]);

  const filteredQuotes = enrichedQuotes.filter(q => {
    const matchesSearch =
      q.numero?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.client?.raison_sociale?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === 'all' || q.displayedStatut === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const totalValue = enrichedQuotes.reduce(
    (sum, q) => sum + Number(q.total_ttc || 0),
    0
  );

  const stats = {
    total: enrichedQuotes.length,
    accepted: enrichedQuotes.filter(q => q.displayedStatut === 'ACCEPTE').length,
    pending: enrichedQuotes.filter(q => q.displayedStatut === 'ENVOYE').length,
    expired: enrichedQuotes.filter(q => q.displayedStatut === 'EXPIRE').length,
  };

  // ================== ACTIONS ==================

  const handleConvertToOrder = async (quote: any) => {
    if (quote.displayedStatut !== 'ACCEPTE') return;
    await createResource(`devis-bcc/${quote.id}/convert-to-bcc`,{});
    fetchQuotes();
  };

    /* ===================== IMPRESSION PROFESSIONNELLE ===================== */
  const handlePrintQuote = (quote: Devis) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error("Impossible d'ouvrir la fenêtre d'impression");
      return;
    }

    // Utilisation des valeurs backend si disponibles, sinon fallback
    const totalTTC = Number(quote.total_ttc);
    const totalHT = quote.total_ht ?? totalTTC / 1.1925;
    const tvaMontant = quote.total_tva;
    const remiseGlobale = Number(quote.remise_globale ?? 0);
    const sousTotalHT = quote.lignes.reduce(
      (sum, l) => sum + Number(l.total_ht || 0),
      0
    );

    const html = `<!DOCTYPE html>
    <html lang="fr">
    <head>
      <meta charset="utf-8">
      <title>Devis ${quote.numero}</title>
      <style>
        body { font-family: 'Segoe UI', Arial, sans-serif; margin: 40px; background: #f9fafb; color: #333; }
        .container { max-width: 900px; margin: 0 auto; background: white; padding: 40px; border-radius: 8px; box-shadow: 0 4px 20px rgba(0,0,0,0.1); }
        .header { text-align: center; padding-bottom: 20px; border-bottom: 3px solid #2563eb; }
        .header h1 { color: #1e40af; margin: 0; font-size: 28px; }
        .header h2 { color: #1e3a8a; margin: 10px 0 0; font-size: 22px; }
        .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin: 30px 0; }
        .info-block h3 { color: #1e40af; border-bottom: 1px solid #dbeafe; padding-bottom: 8px; margin-bottom: 12px; }
        table { width: 100%; border-collapse: collapse; margin: 30px 0; }
        th, td { border: 1px solid #e5e7eb; padding: 12px; text-align: left; }
        th { background: #eff6ff; color: #1e40af; }
        .text-right { text-align: right; }
        .text-center { text-align: center; }
        .totals { float: right; width: 400px; margin-top: 30px; }
        .total-line { display: flex; justify-content: space-between; padding: 8px 0; border-top: 1px solid #e5e7eb; }
        .total-final { font-size: 18px; font-weight: bold; color: #1e40af; border-top: 2px solid #2563eb; padding-top: 12px; }
        .notes { margin-top: 50px; padding: 20px; background: #f3f4f6; border-radius: 8px; }
        .footer { margin-top: 60px; text-align: center; color: #6b7280; font-size: 12px; }
        @media print { body { background: white; margin: 0; } .container { box-shadow: none; padding: 20px; } }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <h1>DEVIS</h1>
          <h2>${quote.numero}</h2>
        </div>

        <div class="info-grid">
          <div class="info-block">
            <h3>Client</h3>
            <div><strong>Raison sociale :</strong> ${quote.client.raison_sociale}</div>
            <div><strong>Code :</strong> ${quote.client.code}</div>
            <div><strong>Téléphone :</strong> ${quote.client.telephone || 'Non renseigné'}</div>
            <div><strong>Email :</strong> ${quote.client.email || 'Non renseigné'}</div>
          </div>
          <div class="info-block">
            <h3>Informations devis</h3>
            <div><strong>Date devis :</strong> ${new Date(quote.date_devis).toLocaleDateString('fr-FR')}</div>
            <div><strong>Valable jusqu'au :</strong> ${new Date(quote.date_validite).toLocaleDateString('fr-FR')}</div>
            <div><strong>Commercial :</strong> ${quote.commercial?.name || 'Non assigné'}</div>
            <div><strong>Statut :</strong> ${quote.statut}</div>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th>Référence</th>
              <th>Désignation</th>
              <th class="text-center">Quantité</th>
              <th class="text-right">Prix U. HT</th>
              <th class="text-right">Remise</th>
              <th class="text-right">Total HT</th>
            </tr>
          </thead>
          <tbody>
            ${quote.lignes.map(l => {
              const remise = l.remise_percentage ? `${l.remise_percentage}%` : 
                            l.remise_montant ? `${l.remise_montant.toFixed(2)} GNF` : '-';
              return `
              <tr>
                <td>${l.reference || '—'}</td>
                <td>${l.designation}</td>
                <td class="text-center">${l.quantite}</td>
                <td class="text-right">${Number(l.prix_unitaire_ht).toLocaleString('fr-FR', {minimumFractionDigits: 2})} GNF</td>
                <td class="text-right">${remise}</td>
                <td class="text-right">${Number(l.total_ht).toLocaleString('fr-FR', {minimumFractionDigits: 2})} GNF</td>
              </tr>`;
            }).join('')}
          </tbody>
        </table>

        <div class="totals">
          <div class="total-line"><span>Sous-total HT :</span><span>${sousTotalHT.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} GNF</span></div>
          ${remiseGlobale > 0 ? `
          <div class="total-line" style="color:#dc2626;"><span>Remise globale :</span>
            <span>- ${remiseGlobale.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} GNF</span>
          </div>
        ` : ''}
          <div class="total-line"><span>Total HT :</span><span>${totalHT.toLocaleString('fr-FR', {minimumFractionDigits: 2})} GNF</span></div>
          <div class="total-line"><span> Total TVA :</span><span>${tvaMontant.toLocaleString('fr-FR', {minimumFractionDigits: 2})} GNF</span></div>
          <div class="total-line total-final"><span>Total TTC :</span><span>${totalTTC.toLocaleString('fr-FR', {minimumFractionDigits: 2})} GNF</span></div>
        </div>
        <div style="clear:both;"></div>

        ${quote.notes_client ? `
          <div class="notes">
            <strong>Notes au client :</strong><br>${quote.notes_client.replace(/\n/g, '<br>')}
          </div>
        ` : ''}

        <div class="footer">
          Document généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR', {hour: '2-digit', minute:'2-digit'})}
        </div>
      </div>
    </body>
    </html>`;

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
      // Ne ferme pas automatiquement pour laisser l'utilisateur choisir
    }, 800);
  };

  /* ===================== EMAIL ===================== */
  const handleSendEmail = async (quote: Devis) => {
    if (quote.statut !== 'BROUILLON') {
      toast.error('Seuls les devis en Brouillon peuvent être envoyés');
      return;
    }

    setSendingId(quote.id);
    toast.loading('Envoi en cours...', { id: 'send-email' });

    try {
      const res = await createResource(`devis-bcc/${quote.id}/envoyer`, {});
      const publicUrl = res.data.public_url || `${window.location.origin}/devis/public/${quote.id}`;

      toast.success(
        <>
          <strong>Devis envoyé avec succès !</strong><br/>
          Lien public : <a href={publicUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Consulter le devis</a>
        </>,
        { duration: 8000, id: 'send-email' }
      );

      // Mise à jour locale du statut
      setQuotes(prev => prev.map(q => q.id === quote.id ? { ...q, statut: 'ENVOYE' as const } : q));
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Erreur lors de l\'envoi', { id: 'send-email' });
    } finally {
      setSendingId(null);
    }
  };

  /* ===================== WHATSAPP ===================== */
  const handleSendWhatsApp = async (quote: Devis) => {
    if (!quote.client.telephone) {
      toast.error('Numéro de téléphone client manquant');
      return;
    }

    try {
      const res = await createResource(`devis-bcc/${quote.id}/preparer-whatsapp`, {});
      const publicUrl = res.data.public_url;

      const message = encodeURIComponent(
        `Bonjour ${quote.client.raison_sociale},\n\n` +
        `Voici votre devis n° ${quote.numero}\n` +
        `Montant total : ${Number(quote.total_ttc).toLocaleString('fr-FR')} GNF\n` +
        `Valable jusqu'au ${new Date(quote.date_validite).toLocaleDateString('fr-FR')}\n\n` +
        `👉 Consultez et acceptez votre devis ici :\n${publicUrl}\n\n` +
        `Merci pour votre confiance !`
      );

      // const telephone = '620103254';
      const phone = quote.client.whatsapp?.replace(/\D/g, '');
      window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
      toast.success('Message WhatsApp ouvert');
    } catch {
      toast.error('Erreur préparation WhatsApp');
    }
  };

  const renegocierDevis = async (devisId: number) => {
    try {
      const { data } = await createResource(
        `devis-bcc/${devisId}/renegocier`,{}
      );

      if (!data?.nouveau_devis_id) {
        toast.error('ID du nouveau devis introuvable');
        return;
      }

      toast.success(data.message || 'Nouveau devis créé');

      // 👉 optionnel : navigation
      // setTimeout(() => {
      //   navigate(`/devis/${data.nouveau_devis_id}`);
      // }, 300);

    } catch (error: any) {
      toast.error(
        error.response?.data?.message ||
        'Impossible de renégocier ce devis'
      );
    }
  };


    if (viewMode === 'detail' && selectedQuote) {
    return (
      <QuoteDetail
        quoteId={selectedQuote.id}
        onBack={() => {
          setViewMode('list');
          setSelectedQuote(null);
          fetchQuotes(); // refresh après éventuelle modification
        }}
        onEdit={onEdit}
      />
    );
  }
  // ================== RENDER ==================

  return (
    <div className="space-y-6">
      <Toaster position="top-right" />

      {/* Filtres */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex flex-col sm:flex-row gap-4">
          <input
            className="flex-1 px-3 py-2 border rounded-md"
            placeholder="Rechercher par numéro ou client"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <select
            className="w-full sm:w-48 px-3 py-2 border rounded-md"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">Tous les statuts</option>
            {Object.keys(STATUS_LABELS).map((s) => (
              <option key={s} value={s}>{STATUS_LABELS[s]}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard title="Total devis" value={stats.total} icon="ri-file-text-line" />
        <StatCard title="Acceptés" value={stats.accepted} icon="ri-check-line" />
        <StatCard title="En attente" value={stats.pending} icon="ri-time-line" />
        <StatCard title="Valeur totale" value={`${totalValue.toLocaleString()} €`} icon="ri-money-euro-circle-line" />
      </div>

      {/* Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y">
            <thead className="bg-gray-50">
              <tr>
                <Th>Numéro</Th>
                <Th>Client</Th>
                <Th>Commercial</Th>
                <Th>Date</Th>
                <Th>Validité</Th>
                <Th>Montant</Th>
                <Th>Statut</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredQuotes.map((q) => (
                <tr key={q.id} className="hover:bg-gray-50">
                  <Td className="font-medium">{q.numero}</Td>
                  <Td>
                    <div className="font-medium">{q.client?.raison_sociale}</div>
                    <div className="text-xs text-gray-500">{q.client?.email}</div>
                  </Td>
                  <Td>{q.commercial?.name ?? '-'}</Td>
                  <Td>{new Date(q.date_devis).toLocaleDateString('fr-FR')}</Td>
                  <Td>
                    <span className={q.isExpired ? 'text-red-600 font-medium' : ''}>
                      {new Date(q.date_validite).toLocaleDateString('fr-FR')}
                    </span>
                    {q.isExpired && <i className="ri-error-warning-line text-red-500 ml-2" />}
                  </Td>
                  <Td className="font-medium">{Number(q.total_ttc).toLocaleString()} €</Td>
                  <Td>
                    <span className={`inline-flex px-2 py-1 text-xs rounded-full ${STATUS_COLORS[q.displayedStatut]}`}>
                      {STATUS_LABELS[q.displayedStatut]}
                    </span>
                  </Td>
                  <Td>
                    <div className="flex gap-2">
                      <IconBtn
                        title="Voir le détail"
                        icon="ri-eye-line"
                        className="text-gray-800"
                        onClick={() => { setSelectedQuote(q); setViewMode('detail'); }}
                      />

                      {/* Modifier : uniquement BROUILLON */}
                      {q.displayedStatut === 'BROUILLON' && (
                        <IconBtn title="Modifier" icon="ri-edit-line" onClick={() => onEdit(q)} />
                      )}

                      {/* Imprimer : toujours autorisé */}
                      <IconBtn title="Imprimer" className={`text-blue-700`} onClick={() => handlePrintQuote(q)} icon="ri-printer-line" />

                      {/* Envoyer : uniquement BROUILLON */}
                      {q.displayedStatut === 'BROUILLON' && (
                        <>
                        <IconBtn
                          title="Envoyer par email"
                          icon="ri-mail-send-line"
                          className={`text-blue-600 ${
                                  sendingId === q.id ? 'opacity-50 cursor-not-allowed' : ''
                                }`}                          
                          onClick={() => handleSendEmail(q)}
                        />
                        <IconBtn
                          title="Envoyer par WhatsApp"
                          icon="ri-whatsapp-line"
                          className={`text-green-600 `}
                          onClick={() => handleSendWhatsApp(q)}
                        />
                        </>
                      )}

                      {['BROUILLON', 'ENVOYE'].includes(q.displayedStatut) && (
                        <IconBtn
                          title="Annuler le devis"
                          icon="ri-close-circle-line"
                          className="text-red-600 hover:text-red-800"
                          onClick={() => {
                            setQuoteToCancel(q);
                            setShowCancelModal(true);
                          }}
                        />

                      )}

                      {/* Convertir en BCC : uniquement ACCEPTE */}
                      {q.displayedStatut === 'ACCEPTE' && (
                        <IconBtn
                          title="Convertir en commande"
                          icon="ri-shopping-cart-line"
                          className="text-purple-600 hover:text-purple-800"
                          onClick={() => {
                            setQuoteToConvert(q);
                            setShowConvertModal(true);
                          }}
                        />
                      )}

                      {q.statut === 'REFUSE' && !q.deja_renegocie && (
                        <IconBtn
                          title="Créer un nouveau devis (renégociation)"
                          icon="ri-refresh-line"
                          className="text-orange-600"
                          onClick={() => renegocierDevis(q.id)}
                        />
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <CancelQuoteModal
          isOpen={showCancelModal}
          quote={quoteToCancel}
          onClose={() => {
            setShowCancelModal(false);
            setQuoteToCancel(null);
          }}
          onCancelled={() => {
            fetchQuotes(); // rafraîchit la liste
          }}
        />
        <ConvertToBCCModal
          isOpen={showConvertModal}
          quote={quoteToConvert}
          onClose={() => {
            setShowConvertModal(false);
            setQuoteToConvert(null);
          }}
          onConverted={() => {
            fetchQuotes(); // refresh liste
          }}
        />
        {!loading && filteredQuotes.length === 0 && (
          <div className="text-center py-12 text-gray-500">Aucun devis trouvé</div>
        )}
      </div>
    </div>
  );
}

// ================== UI COMPONENTS ==================
function StatCard({ title, value, icon }: any) {
  return (
    <div className="bg-white p-4 rounded-lg shadow flex items-center">
      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
        <i className={`${icon} text-blue-600`} />
      </div>
      <div>
        <div className="text-sm text-gray-500">{title}</div>
        <div className="text-lg font-semibold">{value}</div>
      </div>
    </div>
  );
}

function Th({ children }: any) {
  return <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{children}</th>;
}

function Td({ children, className = '' }: any) {
  return <td className={`px-6 py-4 text-sm ${className}`}>{children}</td>;
}

function IconBtn({ icon, title, onClick, className = '' }: any) {
  return (
    <button onClick={onClick} title={title} className={`hover:opacity-80 ${className}`}>
      <i className={icon} />
    </button>
  );
}
